import {
  Badge,
  BodyText,
  Box,
  DetailText,
  Flex,
  Heading,
  Strong,
  ToggleButtonCard,
} from '@utilitywarehouse/hearth-react';
import { TickSmallIcon } from '@utilitywarehouse/hearth-react-icons';
import { useFormikContext } from 'formik';

import {
  ChargeAmount,
  MobileTariff,
  MobileTariffType,
} from '@generated/gql/graphql';
import { formatCurrency } from '@utils/format';
import {
  discountedSimTariff,
  findMultiSimDiscount,
  findUnlimitedMaxDiscount,
  getDiscountDurationMonths,
} from '@utils/mobile';
import { convertValueToCurrency, trimPriceDecimals } from '@utils/price';

import { SimSettingsFormValues } from '../../../types';
import { simTileContents } from './simTileContents';
import './styles.scss';

const formatSimPlanData = (tariff: MobileTariff) => {
  const { monthlyCost } = tariff;
  const discounts = tariff.discounts ?? [];
  const { afterPromoPrice, totalPrice } = discountedSimTariff(tariff);
  const unlimitedMaxPromo = findUnlimitedMaxDiscount(discounts);
  const isDiscount = Boolean(
    findMultiSimDiscount(discounts) || unlimitedMaxPromo
  );

  let discountLengthMonths: Nullable<number> = null;
  if (unlimitedMaxPromo) {
    discountLengthMonths = getDiscountDurationMonths(unlimitedMaxPromo);
  }

  return {
    price: convertValueToCurrency(totalPrice),
    discountedPrice: isDiscount ? monthlyCost : null,
    discountLengthMonths,
    afterPromoPrice: convertValueToCurrency(afterPromoPrice),
    showUnlimitedMaxPromoLegal: !!unlimitedMaxPromo,
  };
};

interface SimPlanTileProps {
  tariff: MobileTariff;
}

interface PriceProps {
  price: ChargeAmount;
  priceLabelId: string;
  discountedPrice: Nullable<ChargeAmount>;
  discountLengthMonths: Nullable<number>;
  afterPromoPrice: ChargeAmount;
  showUnlimitedMaxPromoLegal?: boolean;
}

const Price = ({
  price,
  priceLabelId,
  discountedPrice,
  discountLengthMonths,
  afterPromoPrice,
  showUnlimitedMaxPromoLegal,
}: PriceProps) => {
  if (discountedPrice) {
    return (
      <Box className="service-tile__price">
        <Flex alignItems="baseline" spacing="sm">
          <DetailText size="sm" className="sim-tile__discounted-price">
            {trimPriceDecimals(formatCurrency(discountedPrice))}
          </DetailText>
          <DetailText id={priceLabelId} size="3xl" color="valid">
            {formatCurrency(price)}
            {showUnlimitedMaxPromoLegal && <sup>*</sup>}
          </DetailText>
          {discountLengthMonths && (
            <BodyText>for {discountLengthMonths} months</BodyText>
          )}
        </Flex>
        {showUnlimitedMaxPromoLegal && (
          <BodyText size="sm">
            Then{' '}
            <Strong>
              {trimPriceDecimals(formatCurrency(afterPromoPrice))}
            </Strong>{' '}
            a month, rolling contract
          </BodyText>
        )}
      </Box>
    );
  }

  return (
    <Box className="service-tile__price">
      <Flex alignItems="baseline" spacing="sm">
        <DetailText id={priceLabelId} size="3xl">
          {formatCurrency(price)}
        </DetailText>
        <BodyText size="sm">a month</BodyText>
      </Flex>
      <BodyText size="sm">Monthly rolling contract</BodyText>
    </Box>
  );
};

const SimPlanTile = ({ tariff }: SimPlanTileProps) => {
  const {
    price,
    discountedPrice,
    discountLengthMonths,
    afterPromoPrice,
    showUnlimitedMaxPromoLegal,
  } = formatSimPlanData(tariff);
  const { tariffType } = tariff;
  const content =
    simTileContents[tariffType as keyof typeof simTileContents] ?? [];

  const getElementId = (label: string) => `${tariffType}-${label}`;
  const priceLabelId = getElementId('price');
  const nameLabelId = getElementId('name');
  const descriptionId = getElementId('description');
  const { values } = useFormikContext<SimSettingsFormValues>();
  const isSelected = values.simPlanId === tariff.id;

  return (
    <Flex className="sim-tile">
      {(tariffType === MobileTariffType.UnlimitedMax ||
        tariffType === MobileTariffType.GoUnlimited) && (
        <Badge
          className="sim-tile__sim-badge"
          colorScheme="mobile"
          variant="subtle"
          size="sm"
          flatBase
        >
          £0.00 Multi-SIM offer
        </Badge>
      )}
      <ToggleButtonCard
        id={tariff.id}
        value={tariff.id}
        label={isSelected ? 'Chosen plan' : 'Choose plan'}
        aria-labelledby={`${nameLabelId} ${priceLabelId}`}
        aria-describedby={descriptionId}
        data-testid="mobile-sim-select-card"
      >
        <Flex direction="column" spacing="lg">
          <Heading id={nameLabelId} size="md">
            {content.name}
          </Heading>
          <Price
            price={price}
            priceLabelId={priceLabelId}
            discountedPrice={discountedPrice}
            discountLengthMonths={discountLengthMonths}
            afterPromoPrice={afterPromoPrice}
            showUnlimitedMaxPromoLegal={showUnlimitedMaxPromoLegal}
          />
          <ul className="sim-tile__benefits">
            {content.benefits.map(({ id, content }) => (
              <Flex key={id} asChild alignItems="center" spacing="xs">
                <li>
                  <TickSmallIcon />
                  {content}
                </li>
              </Flex>
            ))}
          </ul>
        </Flex>
      </ToggleButtonCard>
    </Flex>
  );
};

export { SimPlanTile };
